import { attribute, Component, type Emits } from "@bodil/dom/component";
import { animationEnd } from "@bodil/dom/dom";
import { html } from "lit";
import { customElement } from "lit/decorators.js";

import { BtkStackPage } from "./stack-page";

import styles from "./stack.css?inline";

declare global {
    interface HTMLElementTagNameMap {
        "btk-stack": BtkStack;
    }
}

@customElement("btk-stack")
export class BtkStack extends Component {
    emits!: Emits<"change">;

    @attribute accessor direction: "horizontal" | "vertical" = "vertical";
    @attribute({ type: Number }) accessor current = 0;

    get pages(): Array<BtkStackPage> {
        return this.querySlot({ flatten: true, instanceOf: BtkStackPage });
    }

    get currentPage(): BtkStackPage {
        return this.pages[this.current ?? 0];
    }

    get length(): number {
        return this.pages.length;
    }

    push(page: BtkStackPage): Promise<void> {
        const prev = this.pages[this.pages.length - 1];
        prev.parentElement!.append(page);
        return this.toEnd();
    }

    async pop(): Promise<BtkStackPage | undefined> {
        if (this.pages.length > 1) {
            await this.setPage(this.pages.length - 2);
            const page = this.pages[this.pages.length - 1];
            page.remove();
            return page;
        }
        return undefined;
    }

    async stepForward(): Promise<void> {
        if (this.current < this.pages.length - 1) {
            await this.setPage(this.current + 1);
        }
    }

    async stepBack(): Promise<void> {
        if (this.current > 0) {
            await this.setPage(this.current - 1);
        }
    }

    toStart(): Promise<void> {
        return this.setPage(0);
    }

    toEnd(): Promise<void> {
        return this.setPage(this.pages.length - 1);
    }

    async setPage(pageNumber: number): Promise<void> {
        if (pageNumber === this.current) {
            return;
        }
        const oldPage = this.currentPage;
        this.current = pageNumber;
        const newPage = this.currentPage;
        oldPage.classList.remove("active");
        oldPage.inert = true;
        newPage.classList.add("active");
        newPage.inert = false;
        this.emit("change");
        await Promise.all([animationEnd(oldPage), animationEnd(newPage)]);
    }

    protected updatePages() {
        this.pages.forEach((page, index) => {
            page.classList.toggle("active", index === this.current);
            page.inert = index !== this.current;
        });
    }

    protected override firstUpdated() {
        super.firstUpdated();
        this.updatePages();
    }

    protected override render(): unknown {
        return html`<slot></slot>`;
    }

    static styles = styles;
}
